"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseAltiumProjectFile = parseAltiumProjectFile;
const altiumProjectFileTypes_1 = require("./altiumProjectFileTypes");
const altiumBlockParser_1 = require("../common/altiumBlockParser");
const zod_1 = require("zod");
const AltiumDiffPairSuffixSchema = zod_1.default.object({
    Positive: zod_1.default.string(),
    Negative: zod_1.default.string(),
});
function parseVariationString(variationStr) {
    const fields = parsePipeDelimitedFields(variationStr);
    return {
        Designator: fields['Designator'] ?? '',
        UniqueId: fields['UniqueId'] ?? '',
        Kind: Number(fields['Kind'] ?? 0),
        AlternatePart: fields['AlternatePart'] ?? '',
        AltLibLink_DesignItemID: fields['AltLibLink_DesignItemID'],
    };
}
function parsePipeDelimitedFields(str) {
    const fields = {};
    for (const pair of str.split('|')) {
        const eqIndex = pair.indexOf('=');
        if (eqIndex !== -1) {
            fields[pair.substring(0, eqIndex)] = pair.substring(eqIndex + 1);
        }
    }
    return fields;
}
function parseProjectVariant(raw) {
    const variationCount = Number(raw['VariationCount'] ?? 0);
    const variations = [];
    for (let i = 1; i <= variationCount; i++) {
        const variationStr = raw[`Variation${i}`];
        if (variationStr) {
            variations.push(parseVariationString(variationStr));
        }
    }
    const paramVariationCount = Number(raw['ParamVariationCount'] ?? 0);
    const paramVariations = [];
    for (let i = 1; i <= paramVariationCount; i++) {
        const paramVariationStr = raw[`ParamVariation${i}`];
        if (paramVariationStr) {
            const fields = parsePipeDelimitedFields(paramVariationStr);
            paramVariations.push({
                Designator: raw[`ParamDesignator${i}`] ?? '',
                ParameterName: fields['ParameterName'] ?? '',
                VariantValue: fields['VariantValue'] ?? '',
            });
        }
    }
    return {
        Description: raw['Description'] ?? '',
        UniqueId: raw['UniqueId'] ?? '',
        AllowFabrication: raw['AllowFabrication'] === '1',
        ParameterCount: Number(raw['ParameterCount'] ?? 0),
        VariationCount: variationCount,
        ParamVariationCount: paramVariationCount,
        Variations: variations,
        ParamVariations: paramVariations,
    };
}
function parseAnnotateSortOrder(value) {
    const result = altiumProjectFileTypes_1.AnnotateSortOrderSchema.safeParse(value);
    return result.success ? result.data : altiumProjectFileTypes_1.AnnotateSortOrder.AcrossThenUp;
}
function parseAnnotateProcessLocation(value) {
    const result = altiumProjectFileTypes_1.AnnotateProcessLocationSchema.safeParse(value);
    return result.success ? result.data : altiumProjectFileTypes_1.AnnotateProcessLocation.Designator;
}
function parseAltiumProjectFile(filename, contents) {
    const project = (0, altiumProjectFileTypes_1.emptyAltiumProjectFile)(filename);
    let annotate;
    const blocks = (0, altiumBlockParser_1.parseBlocks)(contents);
    for (const block of blocks) {
        switch (block.blockName) {
            case 'Design': {
                const design = block.blockContents;
                design.HierarchyMode = altiumProjectFileTypes_1.HierarchyModeSchema.parse(design.HierarchyMode);
                design.PowerPortNamesTakePriority = altiumProjectFileTypes_1.PowerPortNamesTakePriorityModeSchema.parse(design.PowerPortNamesTakePriority);
                design.NameNetsHierarchically = altiumProjectFileTypes_1.NameNetsHierarchicallyModeSchema.parse(design.NameNetsHierarchically);
                design.ChannelRoomNamingStyle = altiumProjectFileTypes_1.ChannelRoomNamingStyleSchema.parse(design.ChannelRoomNamingStyle);
                design.AnnotateSortOrder = parseAnnotateSortOrder(design.AnnotateSortOrder);
                design.AnnotateProcessLocation = parseAnnotateProcessLocation(design.AnnotateProcessLocation);
                project.Design = design;
                break;
            }
            case 'Annotate':
                annotate = block.blockContents;
                break;
            case 'Preferences':
                project.Preferences = block.blockContents;
                break;
            case 'GeneratedDocument':
                project.GeneratedDocument.push(block.blockContents);
                break;
            case 'Configuration':
                project.Configuration.push(block.blockContents);
                break;
            case 'Document': {
                const doc = block.blockContents;
                const docIndexStr = /^Document(\d+)$/.exec(doc.id)?.[1];
                doc._DocumentIndex = docIndexStr ? parseInt(docIndexStr, 10) : 0;
                project.Document.push(doc);
                break;
            }
            case 'Parameter': {
                const param = block.blockContents;
                const paramIndexStr = /^Parameter(\d+)$/.exec(param.id)?.[1];
                param._Index = paramIndexStr ? parseInt(paramIndexStr, 10) : 0;
                project.Parameter.push(param);
                break;
            }
            case 'ProjectVariant':
                project.ProjectVariant = project.ProjectVariant ?? [];
                project.ProjectVariant.push(parseProjectVariant(block.blockContents));
                break;
            case 'DiffPairSuffix':
                project.DiffPairSuffix ??= [];
                project.DiffPairSuffix.push(AltiumDiffPairSuffixSchema.parse(block.blockContents));
                break;
        }
    }
    if (annotate) {
        if ('SortOrder' in annotate) {
            project.Design.AnnotateSortOrder = parseAnnotateSortOrder(annotate['SortOrder']);
        }
        if ('SortLocation' in annotate) {
            project.Design.AnnotateProcessLocation = parseAnnotateProcessLocation(annotate['SortLocation']);
        }
    }
    return project;
}
//# sourceMappingURL=projectFileParser.js.map